import {
  Component,
  effect,
  inject,
  OnDestroy,
  OnInit,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  ActivatedRouteSnapshot,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  RouterModule,
  RouterOutlet,
} from '@angular/router';
import { Subject } from 'rxjs';
import { Release } from './models/release';
import { AuthService } from './core/services/auth/auth.service';
import { IdleTimeoutService } from './core/services/idle-timeout/idle-timeout.service';
import { ThemeService } from './core/services/theme/theme.service';
import { BreadcrumbService } from './services/breadcrumb/breadcrumb.service';
import { ReleaseContextService } from './services/release-context/release-context.service';
import { ReleaseSwitcherComponent } from './ui/release-switcher/release-switcher.component';
import { UserMenuComponent } from './ui/user-menu/user-menu.component';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, RouterModule, ReleaseSwitcherComponent, UserMenuComponent],
  templateUrl: './app.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  authService = inject(AuthService);
  private breadcrumbService = inject(BreadcrumbService);
  private idleTimeoutService = inject(IdleTimeoutService);
  private themeService = inject(ThemeService);
  private releaseContext = inject(ReleaseContextService);
  breadcrumbs: { label: string; url: string }[] = [];
  /** Drives the top progress bar so a click that triggers a route resolver
      gives immediate feedback instead of appearing to do nothing. */
  readonly isNavigating = signal(false);
  /** Full-bleed views (the execute screen) hide the global header and supply their own. */
  readonly hideAppHeader = signal(false);
  private destroySubject$ = new Subject<void>();

  constructor() {
    this.router.events.subscribe((e) => {
      if (e instanceof NavigationStart) {
        this.isNavigating.set(true);
      } else if (
        e instanceof NavigationEnd ||
        e instanceof NavigationCancel ||
        e instanceof NavigationError
      ) {
        this.isNavigating.set(false);
      }

      if (e instanceof NavigationEnd) {
        const root = this.router.routerState.snapshot.root;
        this.breadcrumbs = this.breadcrumbService.buildBreadcrumbs();
        this.publishReleaseContext(root);
        this.hideAppHeader.set(this.hasRouteFlag(root, 'hideAppHeader'));
      }
    });

    effect(() => {
      if (this.authService.authCheckedSig() && this.authService.isAuthenticatedSig()) {
        this.idleTimeoutService.start();
      }
    });

    this.authService.initialize();
  }

  ngOnInit() {
    this.themeService.initFromSystem();
  }

  /**
   * Tell the header which release we are inside. Every release sub-page resolves
   * `releaseData`, so the resolved tree is the source of truth for those routes.
   * Pages flagged `ownsReleaseContext` (Insights) choose their own release and
   * publish it themselves, so leave whatever they set alone — clearing it here
   * would blank the chip every time they change the release in the query string.
   */
  private publishReleaseContext(snapshot: ActivatedRouteSnapshot | null): void {
    const release = this.findRelease(snapshot);
    if (release) this.releaseContext.setCurrent(release);
    else if (!this.hasRouteFlag(snapshot, 'ownsReleaseContext'))
      this.releaseContext.setCurrent(null);
  }

  /** Walk the resolved route tree for a release payload. */
  private findRelease(snapshot: ActivatedRouteSnapshot | null): Release | null {
    let current = snapshot;
    while (current) {
      const data = current.data?.['releaseData'] as Release | null | undefined;
      if (data?.releaseName) return data;
      current = current.firstChild;
    }
    return null;
  }

  /** True when any route in the active tree sets the given boolean data flag. */
  private hasRouteFlag(snapshot: ActivatedRouteSnapshot | null, key: string): boolean {
    let current = snapshot;
    while (current) {
      if (current.data?.[key] === true) return true;
      current = current.firstChild;
    }
    return false;
  }

  ngOnDestroy() {
    this.destroySubject$.next();
    this.destroySubject$.complete();
  }
}
